/**
 * Profiles Database Module
 *
 * CRUD operations for profile_settings table.
 */

import { supabase } from '@/lib/supabaseClient';
import { withRetry } from '@/utils/retry';
import { handleError } from '@/utils/errorHandler';

export interface ProfileSettings {
  id: string;
  user_id: string;
  org_id: string;
  show_calendar_to_org: boolean;
  show_workload_to_org: boolean;
  show_tasks_to_org: boolean;
  public_share_enabled: boolean;
  public_share_token: string | null;
  public_show_calendar: boolean;
  default_calendar_view: 'month' | 'week' | 'day';
  working_hours_start: string;
  working_hours_end: string;
  created_at: string;
}

export interface ProfileSettingsUI {
  id: string;
  userId: string;
  orgId: string;
  showCalendarToOrg: boolean;
  showWorkloadToOrg: boolean;
  showTasksToOrg: boolean;
  publicShareEnabled: boolean;
  publicShareToken: string | null;
  publicShowCalendar: boolean;
  defaultCalendarView: 'month' | 'week' | 'day';
  workingHoursStart: string;
  workingHoursEnd: string;
  createdAt: string;
}

function convertFromDB(row: ProfileSettings): ProfileSettingsUI {
  return {
    id: row.id,
    userId: row.user_id,
    orgId: row.org_id,
    showCalendarToOrg: row.show_calendar_to_org,
    showWorkloadToOrg: row.show_workload_to_org,
    showTasksToOrg: row.show_tasks_to_org,
    publicShareEnabled: row.public_share_enabled,
    publicShareToken: row.public_share_token,
    publicShowCalendar: row.public_show_calendar,
    defaultCalendarView: row.default_calendar_view,
    workingHoursStart: row.working_hours_start,
    workingHoursEnd: row.working_hours_end,
    createdAt: row.created_at,
  };
}

// --- Queries ---

export async function getProfileSettings(userId: string, orgId: string): Promise<ProfileSettingsUI | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('profile_settings')
      .select('*')
      .eq('user_id', userId)
      .eq('org_id', orgId)
      .maybeSingle()
  );

  if (error) {
    handleError(error, { level: 'warning', message: 'Errore nel caricamento delle impostazioni profilo' });
    return null;
  }

  return data ? convertFromDB(data as ProfileSettings) : null;
}

export async function upsertProfileSettings(
  settings: Partial<ProfileSettingsUI> & { userId: string; orgId: string }
): Promise<ProfileSettingsUI | null> {
  const payload: Record<string, unknown> = {
    user_id: settings.userId,
    org_id: settings.orgId,
  };

  if (settings.showCalendarToOrg !== undefined) payload.show_calendar_to_org = settings.showCalendarToOrg;
  if (settings.showWorkloadToOrg !== undefined) payload.show_workload_to_org = settings.showWorkloadToOrg;
  if (settings.showTasksToOrg !== undefined) payload.show_tasks_to_org = settings.showTasksToOrg;
  if (settings.publicShareEnabled !== undefined) payload.public_share_enabled = settings.publicShareEnabled;
  if (settings.publicShareToken !== undefined) payload.public_share_token = settings.publicShareToken;
  if (settings.publicShowCalendar !== undefined) payload.public_show_calendar = settings.publicShowCalendar;
  if (settings.defaultCalendarView !== undefined) payload.default_calendar_view = settings.defaultCalendarView;
  if (settings.workingHoursStart !== undefined) payload.working_hours_start = settings.workingHoursStart;
  if (settings.workingHoursEnd !== undefined) payload.working_hours_end = settings.workingHoursEnd;

  const { data, error } = await withRetry(() =>
    supabase
      .from('profile_settings')
      .upsert(payload, { onConflict: 'user_id,org_id' })
      .select()
      .single()
  );

  if (error) {
    handleError(error, { level: 'error', message: 'Errore nel salvataggio delle impostazioni profilo' });
    return null;
  }

  return data ? convertFromDB(data as ProfileSettings) : null;
}

export async function generateShareToken(userId: string, orgId: string): Promise<string | null> {
  // Generate a random token
  const token = crypto.randomUUID().replace(/-/g, '').slice(0, 24);

  const result = await upsertProfileSettings({
    userId,
    orgId,
    publicShareEnabled: true,
    publicShareToken: token,
  });

  return result?.publicShareToken ?? null;
}

export async function getPublicProfile(shareToken: string): Promise<ProfileSettingsUI | null> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('profile_settings')
      .select('*')
      .eq('public_share_token', shareToken)
      .eq('public_share_enabled', true)
      .maybeSingle()
  );

  if (error) {
    handleError(error, { level: 'warning', message: 'Errore nel caricamento del profilo pubblico' });
    return null;
  }

  return data ? convertFromDB(data as ProfileSettings) : null;
}

export async function getPublicProfileTasks(userId: string): Promise<unknown[]> {
  const { data, error } = await withRetry(() =>
    supabase
      .from('tasks')
      .select('*')
      .contains('assignees', [userId])
      .order('created_at', { ascending: false })
      .limit(200)
  );

  if (error) {
    handleError(error, { level: 'warning', message: 'Errore nel caricamento dei task del profilo' });
    return [];
  }

  return data || [];
}
